import Phaser from 'phaser';
import { GameState } from '@/state/GameState';
import { hasCutscene } from '@/data/cutscene';
import { dealRoute } from '@/systems/stageFlow';
import { getSaveManager, ACTIVE_SLOT } from '@/systems/SaveManager';

const FONT_MONO = '"Courier New", monospace';
const FONT_IMPACT = 'Impact, "Arial Black", sans-serif';

/** Stage to rewind to when the player accepts the deal for the first time. */
const BETRAYAL_LOOP_STAGE = 6;

/**
 * DealScene — MY-1790
 *
 * Halloway's "Join me?" branch, triggered after Stage 8 clears.
 * Mirrors CharSelectScene input: LEFT/RIGHT to highlight, SPACE/ENTER to confirm.
 *
 * REFUSE → Stage 9 finale (true ending).
 * ACCEPT (1st time) → betrayal cutscene, loop back to Stage 6, tookDeal=1.
 * ACCEPT (2nd time) → sell-out BAD ENDING (CreditsScene variant).
 *
 * Cutscene panels land in a companion ticket; text placeholders are shown
 * when the panel id is absent from the registry.
 */
export class DealScene extends Phaser.Scene {
  private selected: 'refuse' | 'accept' = 'refuse';
  private refuseText!: Phaser.GameObjects.Text;
  private acceptText!: Phaser.GameObjects.Text;
  private confirmed = false;

  constructor() {
    super({ key: 'DealScene' });
  }

  create(): void {
    const { width, height } = this.scale;
    this.confirmed = false;
    this.selected = 'refuse';

    this.buildBackground(width, height);
    this.buildDialogue(width, height);
    this.buildChoices(width, height);
    this.buildHint(width, height);
    this.refreshHighlight();
    this.bindKeys();
  }

  // ─── Background ────────────────────────────────────────────────────────────

  private buildBackground(width: number, height: number): void {
    this.add.rectangle(width / 2, height / 2, width, height, 0x080c12);
    // Amber floor strip
    this.add.rectangle(width / 2, height - 18, width, 36, 0xff6b00).setAlpha(0.06);
    // Outer border
    const border = this.add.graphics();
    border.lineStyle(4, 0xffba08, 0.25);
    border.strokeRect(14, 14, width - 28, height - 28);
  }

  // ─── Dialogue ──────────────────────────────────────────────────────────────

  private buildDialogue(width: number, height: number): void {
    this.add
      .text(width / 2, 52, 'WESTMINSTER BRIDGE — NIGHT', {
        fontFamily: FONT_MONO,
        fontSize: '14px',
        color: '#555e6e',
        letterSpacing: 4,
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.22, 'HALLOWAY', {
        fontFamily: FONT_IMPACT,
        fontSize: '32px',
        color: '#ffba08',
        letterSpacing: 3,
        stroke: '#080c12',
        strokeThickness: 4,
      })
      .setOrigin(0.5);

    // TODO(MY-1790): Replace with companion-ticket cutscene panel when art lands.
    this.add
      .text(
        width / 2,
        height * 0.42,
        '"You\'ve impressed me. I could use someone\nlike you. Walk away now — name your price.\nOr keep fighting for people who don\'t\neven know your name."\n\n"What do you say?"',
        {
          fontFamily: FONT_MONO,
          fontSize: '20px',
          color: '#c8ccd8',
          align: 'center',
          lineSpacing: 8,
          wordWrap: { width: width - 200 },
          stroke: '#080c12',
          strokeThickness: 2,
        },
      )
      .setOrigin(0.5);
  }

  // ─── Choice buttons ────────────────────────────────────────────────────────

  private buildChoices(width: number, height: number): void {
    const y = height * 0.76;
    const gap = 220;

    this.refuseText = this.add
      .text(width / 2 - gap / 2, y, '[ REFUSE ]', {
        fontFamily: FONT_IMPACT,
        fontSize: '28px',
        color: '#06d6a0',
        stroke: '#080c12',
        strokeThickness: 4,
      })
      .setOrigin(0.5);

    this.acceptText = this.add
      .text(width / 2 + gap / 2, y, '[ ACCEPT ]', {
        fontFamily: FONT_IMPACT,
        fontSize: '28px',
        color: '#ef233c',
        stroke: '#080c12',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
  }

  // ─── Hint bar ──────────────────────────────────────────────────────────────

  private buildHint(width: number, height: number): void {
    this.add
      .text(width / 2, height - 32, '← →  Choose      [SPACE / ENTER]  Confirm', {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: '#3a4050',
      })
      .setOrigin(0.5);

    this.add
      .text(width - 10, height - 10, 'MY-1790', {
        fontFamily: FONT_MONO,
        fontSize: '10px',
        color: '#1a1f28',
      })
      .setOrigin(1, 1);
  }

  // ─── Highlight ─────────────────────────────────────────────────────────────

  private refreshHighlight(): void {
    const DIM = 0.35;
    if (this.selected === 'refuse') {
      this.refuseText.setAlpha(1).setScale(1.08);
      this.acceptText.setAlpha(DIM).setScale(1);
    } else {
      this.refuseText.setAlpha(DIM).setScale(1);
      this.acceptText.setAlpha(1).setScale(1.08);
    }
  }

  // ─── Key bindings ──────────────────────────────────────────────────────────

  private bindKeys(): void {
    const kb = this.input.keyboard!;
    kb.on('keydown-LEFT', () => this.onMove('refuse'));
    kb.on('keydown-RIGHT', () => this.onMove('accept'));
    kb.on('keydown-SPACE', () => this.onConfirm());
    kb.on('keydown-ENTER', () => this.onConfirm());
  }

  // ─── Handlers ──────────────────────────────────────────────────────────────

  private onMove(choice: 'refuse' | 'accept'): void {
    if (this.confirmed) return;
    this.selected = choice;
    this.refreshHighlight();
  }

  private onConfirm(): void {
    if (this.confirmed) return;
    this.confirmed = true;

    const prevTookDeal = GameState.current.tookDeal;

    if (this.selected === 'accept') {
      GameState.incrementTookDeal();
      if (prevTookDeal === 0) {
        // First accept: rewind to stage 6 for the betrayal loop
        GameState.resumeAtStage(BETRAYAL_LOOP_STAGE);
      }
      // Persist tookDeal so it survives a save/resume cycle
      getSaveManager().save(ACTIVE_SLOT, { tookDeal: GameState.current.tookDeal }, Date.now());
    }

    const route = dealRoute(
      this.selected,
      prevTookDeal,
      hasCutscene('stage9-intro'),
      hasCutscene('halloway-betrayal'),
      hasCutscene('ending-sellout'),
    );

    this.time.delayedCall(180, () => {
      this.scene.start(route.sceneKey, route.data);
    });
  }
}
